import { createFileRoute, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { getExamsForLevel } from '@/lib/queries';
import { PageSkeleton, ContentRouteError } from '@/components/Skeleton';
import { AppBar } from '@/components/AppBar';
import { ExamYearRow } from '@/components/ExamYearRow';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import type { ExamSummary } from '@/lib/queries';
import type { Level } from '@/lib/types';
import { AppError } from '@/lib/errors';

const VALID_LEVELS: Level[] = ['N1', 'N2', 'N3', 'N4', 'N5'];

function parseLevel(raw: string): Level {
  const upper = raw.toUpperCase() as Level;
  if (!VALID_LEVELS.includes(upper)) {
    throw new AppError('Cấp độ không hợp lệ.', { title: 'Đường dẫn không đúng' });
  }
  return upper;
}

export const Route = createFileRoute('/levels/$level')({
  loader: async ({ params }) => {
    const level = parseLevel(params.level);
    const exams = await getExamsForLevel(level);
    return { level, exams };
  },
  pendingComponent: () => <PageSkeleton />,
  errorComponent: ({ error, reset }) => <ContentRouteError error={error} reset={reset} />,
  component: LevelComponent,
});

function LevelComponent() {
  const { level, exams } = Route.useLoaderData();
  const navigate = useNavigate();
  const matches = useMatches();
  const hasChildMatch = matches.some((m) => m.routeId.startsWith('/levels/$level/'));
  if (hasChildMatch) return <Outlet />;

  const byYear = new Map<string, { jul?: ExamSummary; dec?: ExamSummary }>();
  for (const ex of exams) {
    const [yr, mo] = ex.slug.split('-');
    if (!byYear.has(yr)) byYear.set(yr, {});
    const entry = byYear.get(yr)!;
    if (mo === '07') entry.jul = ex;
    else if (mo === '12') entry.dec = ex;
  }
  const years = Array.from(byYear.keys()).sort().reverse();

  return (
    <PageContainer>
      <AppBar
        title={`JLPT ${level} · Choukai`}
        subtitle={`${exams.length} đề thi · Chọn năm`}
        onBack={() => navigate({ to: '/' })}
      />

      <PageInner className="pt-5">
        <div className="mb-3 flex items-center justify-between px-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
            Năm
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
            Kỳ thi
          </div>
        </div>

        <div className="overflow-hidden rounded-md bg-surface shadow-[var(--shadow-sm)]">
          {years.map((yr, yi) => {
            const e = byYear.get(yr)!;
            return (
              <div
                key={yr}
                className={yi < years.length - 1 ? 'border-b border-border' : ''}
              >
                <ExamYearRow year={yr} jul={e.jul} dec={e.dec} level={level} />
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-center text-[11px] text-ink-3">
          * 07/2020 không tổ chức do COVID-19
        </p>
      </PageInner>
    </PageContainer>
  );
}
